"use client";

import { useEffect, useState } from "react";

import Button from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Textarea from "@/components/ui/textarea";

import { DocumentSummary } from "@/types/document";

import { useSendDocumentTextMutation } from "@/api/provider/user/patient/document-mutation";

interface SendDocumentTextDialogProps {
  document: DocumentSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the updated document after a successful send. */
  onSent?: (document: DocumentSummary) => void;
}

/**
 * Texts a document to the patient with a custom message. Sending also shares the
 * document to the patient portal (the text is a link into the portal) and saves
 * the message as the patient-facing note.
 */
export default function SendDocumentTextDialog({
  document,
  open,
  onOpenChange,
  onSent,
}: SendDocumentTextDialogProps) {
  const [message, setMessage] = useState("");
  const sendMutation = useSendDocumentTextMutation();

  // Pre-fill with any previously sent message when (re)opening for this document.
  useEffect(() => {
    if (open) setMessage(document.patientMessage ?? "");
  }, [open, document.id, document.patientMessage]);

  const handleSend = () => {
    sendMutation.mutate(
      {
        documentId: document.id,
        patientId: document.patientId,
        message: message.trim(),
      },
      {
        onSuccess: (updated) => {
          if (updated) {
            onSent?.(updated);
            onOpenChange(false);
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send to patient via text</DialogTitle>
          <DialogDescription>
            Write a message for the patient. We&apos;ll text it to them with a
            secure link to view this document in their portal, and show the
            message with the document there.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Your recent lab results are ready — everything looks good. Reach out with any questions."
            rows={6}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!message.trim()}
            isLoading={sendMutation.isPending}
          >
            Send text
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
